import Session       from '@/shared/services/session';
import Records       from '@/shared/services/records';
import AbilityService from '@/shared/services/ability_service';
import StanceService from '@/shared/services/stance_service';
import LmoUrlService  from '@/shared/services/lmo_url_service';
import openModal      from '@/shared/helpers/open_modal';
import { I18n }          from '@/i18n';
import { hardReload } from '@/shared/helpers/window';
import BookmarkService from '@/shared/services/bookmark_service';
import { startOfHour, addDays, format } from 'date-fns';

function openSetOutcomeModal(poll) {
  Records.pollTemplates.findOrFetchByKeyOrId(poll.pollTemplateKeyOrId()).then(template => {
    const pollTemplate = template || {
      outcomeStatement: null,
      outcomeStatementFormat: Session.defaultFormat(),
      outcomeReviewDueInDays: null,
    };
    let reviewOn = null
    if (pollTemplate.outcomeReviewDueInDays) {
      reviewOn = format(startOfHour(addDays(new Date(), pollTemplate.outcomeReviewDueInDays)), 'yyyy-MM-dd')
    }
    openModal({
      component: 'PollCommonOutcomeModal',
      props: {
        outcome: Records.outcomes.build({
          groupId: poll.groupId,
          pollId: poll.id,
          statement: pollTemplate.outcomeStatement,
          statementFormat: pollTemplate.outcomeStatementFormat,
          reviewOn: reviewOn,
        })
      }
    });
  });
}

export default new class PollService {

  openSetOutcomeModal(poll) { openSetOutcomeModal(poll) }

  actions(poll, vm, topic_item) {
    if (!poll || !poll.config()) { return {}; }
    return {
      ...BookmarkService.actions(poll),

      translate_poll: {
        icon: 'mdi-translate',
        name: 'common.action.translate',
        dock: 3,
        canPerform() { return AbilityService.canTranslate(poll); },
        perform() {
          poll.pollOptions().forEach((po) => Records.translations.addTo(po, Session.user().locale));
          return Records.translations.addTo(poll, Session.user().locale);
        }
      },

      untranslate_poll: {
        icon: 'mdi-translate',
        name: 'common.action.original',
        dock: 3,
        canPerform() { return AbilityService.canUntranslate(poll); },
        perform() { poll.translationId = null;  poll.pollOptions().forEach((po) => po.translationId = null)}
      },

      edit_stance: {
        name: (poll.config().has_options && 'poll_common.change_vote') || 'poll_common.change_response',
        icon: 'mdi-pencil',
        dock: 2,
        canPerform() { return StanceService.canUpdateStance(poll.myStance()); },
        perform() { return StanceService.updateStance(poll.myStance()); }
      },

      uncast_stance: {
        name: (poll.config().has_options && 'poll_common.remove_your_vote') || 'poll_common.remove_your_response',
        icon: 'mdi-cancel',
        menu: true,
        canPerform() { return StanceService.canUpdateStance(poll.myStance()); },
        perform: () => StanceService.uncastStance(poll.myStance())
      },

      edit_poll: {
        name: 'action_dock.edit_poll_type',
        menu: true,
        nameArgs() { return {pollType: poll.translatedPollType()}; },
        icon: 'mdi-pencil',
        canPerform() { return AbilityService.canEditPoll(poll); },
        to() { return `/p/${poll.key}/edit`; }
      },

      add_to_discussion: {
        icon: 'mdi-comment-plus-outline',
        name: 'action_dock.add_to_discussion',
        menu: true,
        canPerform() {
          const topic = poll.topic();
          return topic_item &&
                 poll.isTopicable() &&
                 topic &&
                 !topic.lockedAt &&
                 topic.adminsInclude(Session.user());
        },
        perform() {
          return openModal({
            component: 'PollCommonAddToDiscussionModal',
            props: { poll }
          });
        }
      },

      make_a_copy: {
        icon: 'mdi-content-copy',
        name: 'templates.make_a_copy',
        menu: true,
        canPerform() { return Session.isSignedIn(); },
        to() { return `/p/new?template_id=${poll.id}`; }
      },

      view_votes: {
        icon: 'mdi-eye-outline',
        name: 'poll_common.view_votes',
        dock: 2,
        canPerform() {
          return !poll.discardedAt && (poll.anonymous
            ? AbilityService.canViewAnonymousVoters(poll)
            : poll.decidedVotersCount > 0);
        },
        to() { return `/p/${poll.key}/votes`; }
      },

      announce_poll: {
        icon: 'mdi-account-multiple-plus',
        name: 'poll_common_form.manage_voters',
        dock: 2,
        canPerform() {
          if (poll.discardedAt || poll.closedAt || (!poll.openedAt && !poll.openingAt)) { return false; }
          return AbilityService.canAddMembersPoll(poll);
        },
        perform() {
          return openModal({
            component: 'PollMembers',
            props: { poll }
          });
        }
      },

      remind_poll: {
        icon: 'mdi-send',
        name: 'common.action.remind',
        dock: 2,
        canPerform() {
          if (poll.discardedAt || poll.closedAt || !poll.openedAt || (poll.votersCount < 2)) { return false; }
          return AbilityService.canAnnouncePoll(poll);
        },
        perform() {
          return openModal({
            component: 'PollReminderForm',
            props: {
              poll: poll.clone()
            }
          });
        }
      },

      close_poll: {
        icon: 'mdi-close-circle-outline',
        name: 'poll_common.close_early',
        dock: 2,
        canPerform() {
          return AbilityService.canClosePoll(poll);
        },
        perform() {

          return openModal({
            component: 'ConfirmModal',
            props: {
              confirm: {
                submit() { return poll.close(); },
                successCallback() {
                  openSetOutcomeModal(poll);
                },
                text: {
                  title: 'poll_common.close_poll_type',
                  helptext: 'poll_common_close_form.helptext',
                  flash: 'poll_common_close_form.poll_type_closed'
                },
                textArgs: {
                  poll_type: poll.translatedPollType(),
                  pollType: poll.translatedPollType()
                }
              }
            }
          });
        }
      },

      reopen_poll: {
        icon: 'mdi-refresh',
        name: 'common.action.reopen',
        dock: 3,
        canPerform() {
          return AbilityService.canReopenPoll(poll);
        },
        perform() {
          return openModal({
            component: 'PollCommonReopenModal',
            props: { poll }});
        }
      },

      // add_comment: {
      //   name: 'activity_card.add_comment',
      //   icon: 'mdi-reply',
      //   menu: true,
      //   canPerform() {
      //     const topic = poll.topic();
      //     return !poll.discardedAt && topic && AbilityService.canAddComment(topic);
      //   },
      //   perform() {
      //     EventBus.$emit('show-add-comment-form');
      //     document.querySelector('#add-comment')?.scrollIntoView({behavior: 'smooth'});
      //   }
      // },

      show_history: {
        icon: 'mdi-history',
        name: 'action_dock.show_edits',
        menu: true,
        canPerform() { return !poll.discardedAt && poll.edited(); },
        perform() {
          return openModal({
            component: 'RevisionHistoryModal',
            props: {
              model: poll
            }
          });
        }
      },

      notification_history: {
        name: 'action_dock.notification_history',
        icon: 'mdi-alarm-check',
        menu: true,
        perform() {
          return openModal({
            component: 'AnnouncementHistory',
            props: {
              model: poll
            }
          });
        },
        canPerform() {
          return !poll.discardedAt &&
                 poll.topic().membersInclude(Session.user());
        }
      },


      export_poll: {
        name: 'common.action.export',
        icon: 'mdi-database-arrow-right-outline',
        menu: true,
        canPerform() {
          return AbilityService.canExportPoll(poll);
        },
        perform() {
          return hardReload(LmoUrlService.poll(poll, {export: 1}, {action: 'export', ext: 'csv', absolute: true}));
        }
      },

      export_poll_blt: {
        name: 'poll_common.export_blt',
        icon: 'mdi-database-export',
        menu: true,
        canPerform() {
          return AbilityService.canExportPoll(poll) && poll.pollType === 'stv';
        },
        perform() {
          return hardReload(LmoUrlService.poll(poll, {export: 1}, {action: 'export', ext: 'blt', absolute: true}));
        }
      },

      // delete_poll:
      //   name: 'common.action.delete'
      //   canPerform: ->
      //     AbilityService.canDeletePoll(poll)
      //   perform: ->
      //     openModal
      //       component: 'ConfirmModal'
      //       props:
      //         confirm:
      //           submit: -> poll.destroy()
      //           text:
      //             title: 'poll_common_delete_modal.title'
      //             confirm: 'poll_common_delete_modal.question'
      //             flash: 'poll_common_delete_modal.success'
      //

      discard_poll: {
        name: 'poll_common.delete_poll',
        menu: true,
        icon: 'mdi-delete',
        canPerform() {
          return !poll.isTopicable() && AbilityService.canDeletePoll(poll);
        },
        perform() {
          return openModal({
            component: 'ConfirmModal',
            props: {
              confirm: {
                submit() { return poll.discard(); },
                text: {
                  raw_title: I18n.global.t('poll_common_delete_modal.title', {pollType: I18n.global.t(poll.pollTypeKey())}),
                  helptext: 'poll_common_delete_modal.question',
                  flash: 'poll_common_delete_modal.success'
                }
              }
            }
          });
        }
      }
    };
  }
};
